import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { access, apiErrorSchema } from './schemas/common.js';
import { userRoleSchema } from './schemas/domain.js';
import { workspaceSchema } from './auth.contract.js';
const c = initContract();
export const inviteSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    role: userRoleSchema,
    expiresAt: z.string(),
});
/**
 * Workspaces (docs/adr/0003-workspaces.md). Creating, choosing and joining
 * need only a signed-in account; inviting is for the workspace's admins and
 * always means the workspace the session is on.
 */
export const workspacesContract = c.router({
    list: {
        method: 'GET',
        path: '/workspaces',
        metadata: access('account'),
        responses: { 200: z.object({ workspaces: z.array(workspaceSchema) }) },
    },
    create: {
        method: 'POST',
        path: '/workspaces',
        metadata: access('account'),
        summary: 'Create a workspace, become its admin and switch to it',
        body: z.object({
            name: z.string().trim().min(1).max(60),
            /** Asked once, at signup: what the new account is called. */
            personName: z.string().trim().min(1).max(80).optional(),
        }),
        responses: {
            201: z.object({ workspace: workspaceSchema }),
            400: apiErrorSchema,
        },
    },
    select: {
        method: 'POST',
        path: '/workspaces/:workspaceId/select',
        metadata: access('account'),
        pathParams: z.object({ workspaceId: z.uuid() }),
        body: z.object({}),
        responses: {
            200: z.object({ workspace: workspaceSchema }),
            404: apiErrorSchema,
        },
    },
    invites: {
        method: 'GET',
        path: '/workspace/invites',
        metadata: access('admin'),
        responses: { 200: z.object({ invites: z.array(inviteSchema) }) },
    },
    invite: {
        method: 'POST',
        path: '/workspace/invites',
        metadata: access('admin'),
        summary: 'Email somebody a link into this workspace',
        body: z.object({ email: z.string().trim().min(3).max(254), role: userRoleSchema }),
        responses: {
            201: z.object({ invite: inviteSchema }),
            400: apiErrorSchema,
            409: apiErrorSchema,
        },
    },
    revokeInvite: {
        method: 'DELETE',
        path: '/workspace/invites/:inviteId',
        metadata: access('admin'),
        pathParams: z.object({ inviteId: z.uuid() }),
        body: c.noBody(),
        responses: { 204: c.noBody() },
    },
    accept: {
        method: 'POST',
        path: '/invites/accept',
        metadata: access('account'),
        summary: 'Join the workspace an invitation is for, and switch to it',
        body: z.object({ token: z.string().min(8).max(128) }),
        responses: {
            200: z.object({ workspace: workspaceSchema }),
            400: apiErrorSchema,
        },
    },
});
//# sourceMappingURL=workspaces.contract.js.map